/**
 * @fileoverview 채널에 종속된 모든 메시지를 조회합니다. 이 함수는 두가지 용도로 쓰입니다.
 * 1. 채널 목록에서 채널을 선택했을때, 이 채널의 모든 메시지 조회
 * 2. sendMessage 함수를 호출한 이후 서버와 채널 상태를 동기화 하기 위해 exponential backoff 로직을 통해 메시지를 조회
 */
import { GetMessagesRequest, Range } from "@daangn/idl/rocketchat/api/api";
import { normalize } from "@data-client/normalizr";
import { isEmpty, isNil, last } from "lodash";
import { store } from "~/rocketchat/redux/store";
import { sendRocketchatRequestWithPromise } from "~/rocketchat/rocketChatClient";
import type { NormalizedResult } from "~/rocketchat/types";
import type { User } from "~/rocketchat/user/user.entity";
import { ChatMessage } from "../chatMessage.entity";
import { chatMessageGroupByChannelIdSelectors } from "../chatMessageSlice";

export async function getMessagesByChannelId(
  params: {
    channelId: string;
    criteriaMessageId?: string;
    limit?: number;
    range?: Range;
  },
  socket: WebSocket,
) {
  const state = store.getState();
  const currentLastMessageId =
    params.criteriaMessageId ??
    last(
      chatMessageGroupByChannelIdSelectors(state.chatMessage)[params.channelId],
    )?.id;

  const response = await sendRocketchatRequestWithPromise({
    socket,
    chatRequest: {
      name: "GetMessagesRequest",
      get_messages: GetMessagesRequest.create({
        /** Required. The ID of the channel */
        channel_id: params.channelId,
        /**
         * @see https://www.notion.so/daangn/for-1e028c3a9f8f809098e0f8364b10c809?pvs=4#1e028c3a9f8f801f9ffde2afa963eea3
         * Required. The ID of the message
         * */
        message_id: currentLastMessageId,
        /**
         * Required. 최대 message 수
         * Max: 100
         */
        limit: params.limit ?? 100,
        // /** Message 기준 operator */
        range: params.range ?? Range.LESS,
      }),
    },
  });

  const getMessagesResponse = response.get_messages;

  if (isNil(getMessagesResponse)) {
    throw new Error("getMessages: response is nil");
  }

  if (
    isEmpty(getMessagesResponse) ||
    getMessagesResponse.messages.length === 0
  ) {
    return {
      messages: null,
      raw: getMessagesResponse,
    };
  }

  const normalizedChatMessage = normalize<
    (typeof ChatMessage)[],
    {
      ChatMessage: NormalizedResult<ChatMessage>;
      User: NormalizedResult<User>;
    }
  >([ChatMessage], getMessagesResponse.messages);

  return {
    messages: normalizedChatMessage.entities.ChatMessage,
    raw: getMessagesResponse,
  };
}
